import api from './api';
import API_ENDPOINTS from '../config/api';
import toast from 'react-hot-toast';

export interface MT5AccountInfo {
  login: number;
  name: string;
  balance?: number;
  equity?: number;
  margin?: number;
  freeMargin?: number;
  leverage?: number;
  currency?: string;
  profit?: number;
  group?: string;
  status?: string;
  enabled?: boolean;
  trade_allowed?: boolean;
  mock?: boolean;
  email?: string;
  phone?: string;
  country?: string;
  city?: string;
  address?: string;
  zip_code?: string;
}

export interface MT5Position {
  order: number;
  symbol: string;
  volume: number;
  type: 'buy' | 'sell';
  openPrice: number;
  currentPrice: number;
  profit: number;
  sl?: number;
  tp?: number;
  comment?: string;
  openTime: string;
}

export interface MT5TradeHistory {
  order: number;
  symbol: string;
  volume: number;
  type: 'buy' | 'sell';
  openPrice: number;
  closePrice: number;
  profit: number;
  commission: number;
  swap: number;
  comment?: string;
  openTime: string;
  closeTime: string;
}

export interface MT5TradeRequest {
  login: number;
  symbol: string;
  volume: number;
  type: 'buy' | 'sell';
  price?: number;
  sl?: number;
  tp?: number;
  comment?: string;
  deviation?: number;
}

export interface MT5BalanceRequest {
  login: number;
  amount: number;
  type: 'deposit' | 'withdraw';
  comment?: string;
}

export interface MT5CreateAccountRequest {
  group: string;
  name: string;
  email: string;
  deposit?: number;
  leverage?: number;
  phone?: string;
  country?: string;
  currency?: string;
  city?: string;
  address?: string;
  zip_code?: string;
}

export interface MT5TransferRequest {
  from: number;
  to: number;
  amount: number;
}

export interface MT5CreditRequest {
  login: number;
  amount: number;
  comment?: string;
}

export interface MT5ModifyTradeRequest {
  ticket: number;
  sl?: number;
  tp?: number;
}

export interface MT5CloseTradeRequest {
  ticket: number;
  volume?: number;
  deviation?: number;
}

export interface MT5CloseAllRequest {
  login: number;
  symbol?: string;
}

export interface MT5PasswordChangeRequest {
  login: number;
  password: string;
  type: 'main' | 'investor' | 'api';
}

export interface MT5AccountActionRequest {
  login: number;
}

export interface MT5ChangeLeverageRequest {
  login: number | string;
  leverage: number | string;
  reason: string;
}

export interface MT5Transaction {
  currency: unknown;
  id: number;
  transaction_id: string;
  crm_user_id: number;
  mt5_login: number;
  type: 'deposit' | 'withdrawal' | 'transfer' | 'credit' | 'credit_remove';
  amount: string;
  comment: string;
  status: 'pending' | 'completed' | 'failed';
  from_login: number | null;
  to_login: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface MT5TransactionsResponse {
  success: boolean;
  data: {
    transactions: MT5Transaction[];
  };
}

export const mt5Api = {
  getAccounts: async (group?: string): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.MT5.ACCOUNT.GET_LIST(group));
      const responseData = response.data;
      if (Array.isArray(responseData)) {
        return { data: responseData };
      }

      if (responseData.success && responseData.data) {
        return responseData;
      }

      return responseData;
    } catch (error: any) {
      toast.error('Failed to fetch MT5 accounts');
      throw error;
    }
  },
  getAccountInfo: async (login: string | number): Promise<any> => {
    try {
      const response = await api.get(
        API_ENDPOINTS.MT5.ACCOUNT.GET_SINGLE(login)
      );
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return responseData.data;
      }

      return responseData;
    } catch (error: any) {
      toast.error('Failed to fetch MT5 account information');
      throw error;
    }
  },

  getUser: async (login: string | number): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.MT5.GET_USER(login));
      const responseData = response.data;
      if (responseData.success && responseData.data) {
        return responseData.data;
      }
      return responseData;
    } catch (error: any) {
      toast.error('Failed to fetch MT5 user details');
      throw error;
    }
  },

  getBalance: async (login: string | number): Promise<any> => {
    try {
      const response = await api.get(
        API_ENDPOINTS.MT5.ACCOUNT.GET_SINGLE(login)
      );
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return responseData.data;
      }

      return responseData;
    } catch (error: any) {
      toast.error('Failed to fetch MT5 account balance');
      throw error;
    }
  },
  getGroups: async (): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.MT5.GET_GROUPS);
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return responseData.data;
      }

      return responseData;
    } catch (error: any) {
      toast.error('Failed to fetch MT5 groups');
      throw error;
    }
  },

  getTransactions: async (
    type?: string,
    status?: string
  ): Promise<MT5TransactionsResponse> => {
    try {
      const response = await api.get(
        API_ENDPOINTS.MT5.TRANSACTIONS(type, status)
      );
      const responseData = response.data;

      if (responseData.success && responseData.data) {
        return responseData;
      }

      return responseData;
    } catch (error: any) {
      toast.error('Failed to fetch transactions');
      throw error;
    }
  },

  getLeverageHistory: async (): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.MT5.LEVERAGE_HISTORY);
      const responseData = response.data;

      if (Array.isArray(responseData)) {
        return responseData;
      }

      if (responseData.success && responseData.data) {
        return responseData.data;
      }

      return responseData;
    } catch (error: any) {
      toast.error('Failed to fetch leverage history');
      throw error;
    }
  },

  getLeverageHistoryByLogin: async (login: string | number): Promise<any> => {
    try {
      const response = await api.get(
        API_ENDPOINTS.MT5.LEVERAGE_HISTORY_BY_LOGIN(login)
      );
      const responseData = response.data;

      if (Array.isArray(responseData)) {
        return responseData;
      }

      if (responseData.success && responseData.data) {
        return responseData.data;
      }

      return responseData;
    } catch (error: any) {
      toast.error('Failed to fetch user leverage history');
      throw error;
    }
  },

  changeLeverage: async (payload: MT5ChangeLeverageRequest): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.MT5.CHANGE_LEVERAGE, {
        login: Number(payload.login),
        leverage: Number(payload.leverage),
        reason: payload.reason,
      });
      const responseData = response.data;

      if (responseData.success === false) {
        throw new Error(
          responseData.error ||
            responseData.message ||
            'Failed to change leverage'
        );
      }

      if (responseData.success && responseData.data) {
        if (responseData.data.success === false) {
          throw new Error(
            responseData.data.error || 'Failed to change leverage'
          );
        }
        return responseData.data;
      }

      return responseData;
    } catch (error: any) {
      toast.error(error.message || 'Failed to change leverage');
      throw error;
    }
  },
};

export default mt5Api;
